import { NgFor, NgIf } from '@angular/common';
import {
	Component,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Optional
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SecondaryButtonComponent, TypographyComponent } from 'components';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { ReportFigureFlagsService } from '../../../../services/report-figure-flags.service';
import { ReportLanguageService } from '../../../../services/report-language.service';
import {
	ReportMessageLanguage,
	reportMessage
} from '../../../../services/report-messages';
import { ReportFigureFlag } from '../../../../services/report-quoted-figures';

/** One flag, worded. */
interface ReportFigureFlagLine {
	flag: ReportFigureFlag;
	sentence: string;
	origin: string;
	updateLabel: string;
	keepLabel: string;
}

/**
 * The flags under one text of the report: a line for each number in it that
 * quotes a figure the live data has since moved on from — "This number is now
 * 1,352." — with Update, which writes the new figure in, and Keep, which hides
 * the flag until the figure moves again.
 *
 * EDITOR ONLY, by construction: the flags come from the service only the
 * editor page provides, so the PDF, a share link and present mode draw nothing
 * here.
 */
@Component({
	standalone: true,
	selector: 'app-report-figure-flags',
	templateUrl: './report-figure-flags.component.html',
	styleUrls: ['./report-figure-flags.component.scss'],
	imports: [
		NgFor,
		NgIf,
		MatTooltipModule,
		TypographyComponent,
		SecondaryButtonComponent
	]
})
export class ReportFigureFlagsComponent
	implements OnInit, OnChanges, OnDestroy
{
	/** The text's place (`figurePlace`); null draws nothing. */
	@Input() place: string | null = null;

	lines: ReportFigureFlagLine[] = [];
	updateText = '';
	keepText = '';

	private readonly place$ = new BehaviorSubject<string | null>(null);
	private readonly destroy$ = new Subject<void>();

	constructor(
		@Optional() private figureFlags: ReportFigureFlagsService | null,
		@Optional() private reportLanguage: ReportLanguageService | null
	) {}

	ngOnInit(): void {
		const language$: Observable<ReportMessageLanguage> =
			this.reportLanguage?.language$ ?? of('en');
		combineLatest([
			this.place$.pipe(
				distinctUntilChanged(),
				switchMap(place =>
					this.figureFlags && place
						? this.figureFlags.flagsFor(place)
						: of<ReportFigureFlag[]>([])
				)
			),
			language$
		])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([flags, language]) => {
				this.updateText = reportMessage(language, 'flag.update');
				this.keepText = reportMessage(language, 'flag.keep');
				this.lines = flags.map(flag => wordFlag(flag, language));
			});
	}

	ngOnChanges(): void {
		this.place$.next(this.place);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	trackByLine(_index: number, line: ReportFigureFlagLine): string {
		return `${line.flag.key}|${line.flag.quoted}`;
	}

	update(line: ReportFigureFlagLine): void {
		this.figureFlags?.update(line.flag);
	}

	keep(line: ReportFigureFlagLine): void {
		this.figureFlags?.keep(line.flag);
	}
}

function wordFlag(
	flag: ReportFigureFlag,
	language: ReportMessageLanguage
): ReportFigureFlagLine {
	const params = { quoted: flag.quoted, now: flag.now };
	return {
		flag,
		sentence: reportMessage(language, 'flag.now', params),
		origin: reportMessage(language, 'flag.origin', params),
		updateLabel: reportMessage(language, 'flag.update_label', params),
		keepLabel: reportMessage(language, 'flag.keep_label', params)
	};
}
